import { Box, Flex, HStack, HTMLChakraProps, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import LogoWord from '@/assets/icons/ic-logo.svg';

import { MOBILE_NAV_PADDING_X } from '@/components/ui/shared/headers/CoreHeader/components/constants';
import { DropdownMenuLibrary } from '@/components/ui/shared/headers/CoreHeader/components/DropdownMenuLibrary';
import { DropdownMenuSolutions } from '@/components/ui/shared/headers/CoreHeader/components/DropdownMenuSolutions';
import { UserMenu } from '@/components/ui/shared/headers/CoreHeader/components/UserMenu';
import { InstitutionLabel } from '@/components/ui/shared/InstitutionLabel/InstitutionLabel';
import { NavLink } from '@/components/ui/shared/links/NavLink/NavLink';
import {
  CoreNavigation,
  CoreNavigationActions,
  CoreNavigationBrand,
  CoreNavigationClose,
  CoreNavigationHamburger,
  CoreNavigationNav,
  ICoreNavigationProps,
} from '@/components/ui/shared/navigations/CoreNavigation/CoreNavigation';
import { AccessFrom } from '@/enums/AccessFrom';

export interface ICoreHeaderProps extends HTMLChakraProps<'div'> {
  variant?: ICoreNavigationProps['variant'];
  onLogout: () => void;
  accessFrom: AccessFrom;
}

export const CoreHeader: FC<ICoreHeaderProps> = ({ onLogout, variant = 'ghost', accessFrom, ...rest }) => {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <CoreNavigation variant={variant} {...rest}>
      <CoreNavigationHamburger title={t('action.openMenu')} aria-label={t('action.openMenu')} />

      <CoreNavigationBrand w={{ base: 'auto', lg: 'full' }} h="auto" gap="16px">
        <NextLink href="/" aria-label="Go to the homepage" passHref>
          <LogoWord css={{ height: '1.25rem' }} />
        </NextLink>

        <HStack spacing="8px">
          <Box aria-hidden bg="currentColor" w="2px" h="48px" flexShrink={0} />
          <Text fontFamily="inter" fontSize="10px" lineHeight="16px" fontWeight="semibold" whiteSpace="nowrap">
            <Box as="span" display="block">
              FROM
            </Box>
            <Box as="span" display="block">
              ANNUAL REVIEWS
            </Box>
          </Text>
        </HStack>
      </CoreNavigationBrand>

      <CoreNavigationNav>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          mt={{ base: 5, lg: 0 }}
          alignItems={{ base: 'flex-start', lg: 'center' }}
        >
          {/* the label shares the dropdowns' right gutter, so both land on the same edge */}
          <Flex display={{ base: 'flex', lg: 'none' }} w="full" alignItems="center" justifyContent="space-between">
            <CoreNavigationClose title={t('action.closeMenu')} aria-label={t('action.closeMenu')} />
            <InstitutionLabel
              trackAccess={false}
              pr={MOBILE_NAV_PADDING_X}
              fontSize="16px"
              lineHeight="22px"
              flexShrink={0}
            />
          </Flex>

          <DropdownMenuLibrary />

          <NextLink href="/library/events-list" passHref legacyBehavior>
            <NavLink isActiveLink={router.asPath.startsWith('/library/events-list')}>
              {t('eventsPage.navigationLabel')}
            </NavLink>
          </NextLink>

          <DropdownMenuSolutions />

          {/*
            The top border is the drawer's divider. The column aligns children to
            flex-start, so without stretching, the rule is only as wide as the
            profile links under it. Applied here rather than in CoreNavigationActions'
            baseStyle, which the Events, Hubs and Publisher headers also use.
          */}
          <CoreNavigationActions alignSelf={{ base: 'stretch', lg: 'auto' }} w={{ lg: 'auto' }}>
            <UserMenu accessFrom={accessFrom} onLogout={onLogout} />
          </CoreNavigationActions>
        </Flex>
      </CoreNavigationNav>
      <InstitutionLabel
        px="16px"
        fontSize="16px"
        lineHeight="22px"
        flexShrink={0}
        display={{ base: 'none', lg: 'block' }}
      />
    </CoreNavigation>
  );
};
